// --------------------------------------------------------------
// Created On: 2025-10-29
// Author: Zachary Thomas
//
// Last Modified: 2026-10-01
// Modified By: Zachary Thomas
//
// Copyright 2026 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RPM_COMPANY } from "src/constants/miscellaneous";
import deepCopy from "src/utilities/deepCopy";

let initialState: CurrentUser = {
  userId: 0,
  userName: "",
  emailAddress: "",
  originalCompanyId: 0,
  originalCompanyName: "",
  originalPermissions: [],
  companyId: 0,
  companyName: "",
  userCompanyPath: [],
  isCompanyHidden: true,
  permissions: [],
  isPackager: false,
  mfaEnabled: false,
};

const localStorageUser = localStorage.getItem("reduxCurrentUser");
if (localStorageUser !== null) {
  try {
    initialState = JSON.parse(localStorageUser) as CurrentUser;
  } catch {
    console.error("Failed to restore current user information on application load.");
  }
}

// A Redux slice for information about the current logged in user.
const currentUserSlice = createSlice({
  name: "currentUser",
  initialState,
  reducers: {
    setCurrentUser: (
      state,
      action: PayloadAction<{
        userId: number;
        userName: string;
        emailAddress: string;
        originalCompanyId: number;
        originalCompanyName: string;
        originalPermissions: string[];
        companyId: number;
        companyName: string;
        userCompanyPath: UserCompanyPathSegment[];
        isCompanyHidden: boolean;
        permissions: string[];
        isPackager: boolean;
        mfaEnabled: boolean;
      }>
    ) => {
      const currentUser = {
        userId: action.payload.userId,
        userName: action.payload.userName,
        emailAddress: action.payload.emailAddress,
        originalCompanyId: action.payload.originalCompanyId,
        originalCompanyName: action.payload.originalCompanyName,
        originalPermissions: action.payload.originalPermissions,
        companyId: action.payload.companyId,
        companyName: action.payload.companyName,
        userCompanyPath: state.userCompanyPath,
        isCompanyHidden: action.payload.isCompanyHidden,
        permissions: action.payload.permissions,
        isPackager: action.payload.isPackager,
        mfaEnabled: action.payload.mfaEnabled,
      };
      localStorage.setItem("reduxCurrentUser", JSON.stringify(currentUser));
      return currentUser;
    },
    returnToOriginalCompany: (state) => {
      const currentUser = {
        userId: state.userId,
        userName: state.userName,
        emailAddress: state.emailAddress,
        originalCompanyId: state.originalCompanyId,
        originalCompanyName: state.originalCompanyName,
        originalPermissions: state.originalPermissions,
        companyId: state.originalCompanyId,
        companyName: state.originalCompanyName,
        userCompanyPath: [],
        isCompanyHidden: state.isCompanyHidden,
        permissions: state.originalPermissions,
        isPackager: state.originalCompanyName === RPM_COMPANY ? false : state.isPackager,
        mfaEnabled: state.mfaEnabled,
      };
      localStorage.setItem("reduxCurrentUser", JSON.stringify(currentUser));
      return currentUser;
    },
    returnToPreviousCompany: (state) => {
      let previousCompanyId = state.originalCompanyId;
      let previousCompanyName = state.originalCompanyName;
      let userCompanyPathWithoutLastElement: UserCompanyPathSegment[] = [];

      // The last company in the path is the current company,
      // so we want to return to the second to last company in the path.
      if (state.userCompanyPath.length > 1) {
        const secondToLastCompanyInPath = state.userCompanyPath[state.userCompanyPath.length - 2];
        previousCompanyId = secondToLastCompanyInPath.companyId;
        previousCompanyName = secondToLastCompanyInPath.companyName;
        userCompanyPathWithoutLastElement = state.userCompanyPath.slice(0, -1);
      }

      const aboutToReturnToRpmCompany =
        userCompanyPathWithoutLastElement.length === 0 && state.originalCompanyName === RPM_COMPANY;

      const currentUser = {
        userId: state.userId,
        userName: state.userName,
        emailAddress: state.emailAddress,
        originalCompanyId: state.originalCompanyId,
        originalCompanyName: state.originalCompanyName,
        originalPermissions: state.originalPermissions,
        companyId: previousCompanyId,
        companyName: previousCompanyName,
        userCompanyPath: userCompanyPathWithoutLastElement,
        isCompanyHidden: state.isCompanyHidden,
        permissions: userCompanyPathWithoutLastElement.length > 0 ? state.permissions : state.originalPermissions,
        isPackager: aboutToReturnToRpmCompany ? false : state.isPackager,
        mfaEnabled: state.mfaEnabled,
      };
      localStorage.setItem("reduxCurrentUser", JSON.stringify(currentUser));
      return currentUser;
    },
    stepIntoCompany: (
      state,
      action: PayloadAction<{ companyId: number; companyName: string; isPackager: boolean; permissions: string[] }>
    ) => {
      let newUserCompanyPath: UserCompanyPathSegment[] = [];
      // Only load the state of the company path if it exists and we are not currently at the original company.
      if (state.userCompanyPath !== undefined && state.originalCompanyId !== state.companyId) {
        newUserCompanyPath = deepCopy(state.userCompanyPath);
      }
      newUserCompanyPath.push({
        companyId: action.payload.companyId,
        companyName: action.payload.companyName,
      });
      const newCompanyId = action.payload.companyId;
      const newCompanyName = action.payload.companyName;

      const currentUser = {
        userId: state.userId,
        userName: state.userName,
        emailAddress: state.emailAddress,
        originalCompanyId: state.originalCompanyId,
        originalCompanyName: state.originalCompanyName,
        originalPermissions: state.originalPermissions,
        companyId: newCompanyId,
        companyName: newCompanyName,
        userCompanyPath: newUserCompanyPath,
        isCompanyHidden: state.isCompanyHidden,
        permissions: action.payload.permissions,
        isPackager: action.payload.isPackager,
        mfaEnabled: state.mfaEnabled,
      };
      localStorage.setItem("reduxCurrentUser", JSON.stringify(currentUser));
      return currentUser;
    },
  },
});

export const { setCurrentUser, returnToOriginalCompany, returnToPreviousCompany, stepIntoCompany } =
  currentUserSlice.actions;
export default currentUserSlice.reducer;
